import { Component, ElementRef, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { Subscription, concatMap, first, from, fromEvent, takeUntil, tap } from 'rxjs';
import { IonicComponentsModule } from 'src/app/modules/ionic-components.module';
import { UtilService } from 'src/app/services/util.service';

export type CanvasStroke = {
  lines: {x: number, y: number}[],
  color: string
}

@Component({
  selector: 'fv-canvas-drawing',
  templateUrl: './canvas-drawing.component.html',
  styleUrls: ['./canvas-drawing.component.scss'],
  standalone: true,
  imports: [
    IonicComponentsModule
  ]
})
export class CanvasDrawingComponent {

  //#region Colores del trazo

  public colores = ['#212121', '#2154c0', '#009688', 
                     '#fff307', '#ff9800', '#f00136', '#ffffff']
  public selectedColor = '#212121'

  //#endregion

  //#region Propiedades

  @Input("width") public width: number = null
  @Input("height") public height: number = null
  @ViewChild("canvas", {static: false}) public canvas: ElementRef<HTMLCanvasElement>
  private cx: CanvasRenderingContext2D
  public strokes: CanvasStroke[] = []
  public currentStroke: CanvasStroke = null
  private pointerDrawSubscription: Subscription
  private pointerDownSubscription: Subscription
  private pointerUpSubscription: Subscription
  private img: HTMLImageElement = null
  public modoEdicion: boolean = true
  @Input("allow-clear-image") public AllowClearImage: boolean = false
  @Input("hide-colors") public hideColors: boolean = false
  @Input("stoke-width") public strokeWidth: number = 2
  @Input("new-image-background") public newImageBackground = 'white'

  @Input("background-image") public set backgroundImage(url: string) {
    console.log(`Se quiere cargar la imagen`)
    this.img = null
    const img: HTMLImageElement = new Image()
    fromEvent(img, 'onload').pipe(first()).subscribe(async () => {
      console.log(`Se ha cargado la imagen`)
      this.img = img
      this.width = img.width
      this.height = img.height
      this.canvas.nativeElement.setAttribute('width', img.width.toString())
      this.canvas.nativeElement.setAttribute('height', img.height.toString())
      await this.util.Sleep(1)
      this.updateCanvas()
    })
    fromEvent(img, 'onerror').pipe(first()).subscribe(() => {
      console.log(`Ha fallado la carga`)
      this.img = null
      this.width = img.width
      this.height = img.height
      this.canvas.nativeElement.setAttribute('width', img.width.toString())
      this.canvas.nativeElement.setAttribute('height', img.height.toString())
      this.updateCanvas()
    })
    img.src = url
    if (img.complete) {
      this.img = img
      this.width = img.width
      this.height = img.height
      this.util.Sleep(1).then(() => {
        if (this.canvas?.nativeElement != null) {
          this.canvas.nativeElement.setAttribute('width', img.width.toString())
          this.canvas.nativeElement.setAttribute('height', img.height.toString())
          this.updateCanvas()
        }
      })
    }
  }

  //#endregion

  //#region Constructor

  constructor(
    private util: UtilService,
    private ngZone: NgZone
  ) { }

  //#endregion

  //#region Init

  public initialize(): void {
    
    const canvasEl = this.canvas.nativeElement
    this.cx = canvasEl.getContext('2d');

    [this.pointerDrawSubscription, this.pointerDownSubscription, this.pointerUpSubscription].forEach(p => p?.unsubscribe())

    canvasEl.addEventListener('touchstart', (event) => {
      if (this.modoEdicion) {
        event.preventDefault();
      }
    }, { passive: false });

    const pointerDown = fromEvent(this.canvas.nativeElement, 'pointerdown')
    const pointerMove = fromEvent(this.canvas.nativeElement, 'pointermove')
    const pointerUp = fromEvent(this.canvas.nativeElement, 'pointerup')

    pointerDown.pipe(
      concatMap(() => pointerMove.pipe(takeUntil(pointerUp)))
    )

    const pointerDraw = pointerDown.pipe(
      tap((e: PointerEvent) => {
        if (this.modoEdicion) {
          this.currentStroke = {
            color: this.selectedColor,
            lines: []
          }
          this.strokes.push(this.currentStroke)
        }
      }),
      concatMap(() => pointerMove.pipe(takeUntil(pointerUp)))
    )

    this.pointerDrawSubscription = pointerDraw.subscribe((e: MouseEvent) => {
      if (this.modoEdicion) {
        this.draw(e.offsetX, e.offsetY)
      }
    })
    this.pointerDownSubscription = pointerDown.subscribe((e: PointerEvent) => {
      if (this.modoEdicion) {
        canvasEl.setPointerCapture(e.pointerId)
        const canvasRect = this.canvas.nativeElement.getBoundingClientRect()
        let coords = {x: e.offsetX / canvasRect.width, y: e.offsetY / canvasRect.height}
        this.cx.moveTo(coords.x * this.width, coords.y * this.height)
        this.cx.strokeStyle = this.selectedColor
        this.cx.beginPath()
      }
    })
    this.pointerUpSubscription = pointerUp.subscribe((e: PointerEvent) => {
      if (this.modoEdicion) {
        canvasEl.releasePointerCapture(e.pointerId)
        this.updateCanvas()
      }
    })

    this.updateCanvas()
  }

  ngAfterViewInit(): void {
    if (this.canvas?.nativeElement != null) {
      this.initialize()
    }
  }

  ngOnDestroy(): void {
    [this.pointerDrawSubscription, this.pointerDownSubscription, this.pointerUpSubscription].forEach(p => p?.unsubscribe())
  }

  //#endregion

  //#region Draw

  public draw(offsetX, offsetY): void {
    const canvasRect = this.canvas.nativeElement.getBoundingClientRect()
    let coords = {x: offsetX / canvasRect.width, y: offsetY / canvasRect.height}
    this.currentStroke?.lines.push(coords)

    this.cx.lineTo(coords.x * this.width, coords.y * this.height)
    this.cx.lineWidth = this.width / canvasRect.width
    this.cx.stroke()
  }

  public updateCanvas(): void {
    const canvasRect = this.canvas.nativeElement.getBoundingClientRect()
    this.cx.clearRect(0, 0, this.width, this.height)
    if (this.img != null) this.cx.drawImage(this.img, 0, 0, this.width, this.height)
    this.strokes.forEach(stroke => {
      if (stroke.lines?.length > 1) {
        this.cx.beginPath();
        this.cx.strokeStyle = stroke.color
        this.cx.moveTo(stroke.lines[0].x * this.width, stroke.lines[0].y * this.height);
        for (let i = 1; i < stroke?.lines.length; i++) {
          this.cx.lineTo(stroke.lines[i].x * this.width, stroke.lines[i].y * this.height);
        }
        this.cx.lineWidth = this.strokeWidth * this.width / canvasRect.width
        this.cx.stroke();
      }
    });
  }

  public removeLastStroke(): void {
    this.strokes.splice(-1, 1)
    this.updateCanvas()
  }

  public toDataUrl(): string {
    return this.canvas?.nativeElement.toDataURL('image/png', 90)
  }

  //#endregion

  //#region Clear image

  public clearImage(): void {
    this.ngZone.run(() => {
      this.backgroundImage = this.util.createWhiteImage(this.width, this.height, this.newImageBackground)
      this.strokes = []
      this.updateCanvas()
    })
  }

  //#endregion

}
